// AI Receptionist in-agent card capture (SGL-95).
//
// The signed-in counterpart to businessCardService.ts. That one talks to the
// PUBLIC /card-to-crm/* Lambdas: a shared secret compiled into this bundle, and
// a 5-scan-per-IP-per-day counter shared with the marketing site (one NAT'd
// office exhausts it for everyone). These routes are Cognito-authorized and
// customer-scoped instead — no embedded secret, no daily cap.
//
// Routes live on the same AIAgentApi Gateway as /ai-agents, so the stage root
// is derived from the agent API URL rather than adding another Amplify build
// var to keep in sync (same reasoning as services/contactImportService.ts).
import axios from "axios";
import { getApiBaseUrl } from "../../utils/urlHandler";
import { CardIssue, Contact, ExtractedContact } from "./types/cardToCrm.types";

const AGENT_API_URL = getApiBaseUrl("REACT_APP_AGENT_API_URL");
const API_ROOT = AGENT_API_URL.replace(/\/ai-agents\/?$/, "");

/** Mirrors MAX_IMAGES / MAX_CONTACTS in the receptionist_card_* Lambdas.
 *  Raising it here alone just moves where the rejection happens — the Lambdas
 *  enforce their own copy. */
export const MAX_CARDS_PER_SCAN = 10;

const requireCustomer = (customerId: string): string => {
  const id = (customerId || "").trim();
  if (!id) {
    throw new CardSaveError(
      "unknown",
      "No customer is selected, so cards can't be scanned or saved."
    );
  }
  return id;
};

const authHeaders = (idToken: string) => ({
  headers: { Authorization: `Bearer ${idToken}`, "Content-Type": "application/json" },
});

const newId = (): string =>
  typeof crypto !== "undefined" && "randomUUID" in crypto
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(16).slice(2)}`;

/** What the save endpoints report back for one contact. */
export interface CardSaveOutcome {
  ok: boolean;
  ghl: {
    contact_id: string | null;
    is_new: boolean | null;
    email_eligible?: boolean;
    tagged?: boolean | null;
    /** The contact's full tag list as GHL reports it after the write. */
    tags?: string[] | null;
  } | null;
  /** null when Bedrock couldn't draft — the contact is still saved. */
  draft: { subject: string; body: string } | null;
  /** null = sending wasn't attempted; true/false = attempted, and the result. */
  sent: boolean | null;
  sendError: string | null;
  error: string | null;
}

/** The GHL-connection failures the UI renders differently from a generic error. */
export type GhlConnectionProblem = "not_connected" | "needs_reauth" | "unavailable";

export class CardSaveError extends Error {
  reason: GhlConnectionProblem | "unknown";
  constructor(reason: CardSaveError["reason"], message: string) {
    super(message);
    // tsconfig targets es5, where extending a built-in leaves the instance on
    // Error.prototype — `instanceof CardSaveError` would silently be false.
    // Callers read `.reason`, but restore the chain so a future `instanceof`
    // isn't a trap.
    Object.setPrototypeOf(this, CardSaveError.prototype);
    this.reason = reason;
  }
}

const MESSAGES: Record<CardSaveError["reason"], string> = {
  not_connected: "This customer isn't connected to GoHighLevel yet.",
  needs_reauth: "The GoHighLevel connection needs reauthorizing.",
  unavailable: "Couldn't reach GoHighLevel. Nothing was written — try again shortly.",
  unknown: "Couldn't save to GoHighLevel. Try again in a moment.",
};

const toExtractError = (err: any): Error => {
  const status = err?.response?.status;
  if (status === 403 || status === 401) {
    return new Error("You don't have access to this customer's card scanning.");
  }
  if (status === 400) {
    return new Error(
      "That image couldn't be processed — try a clearer photo, or fewer cards at once."
    );
  }
  if (status === 413) {
    return new Error("Those photos are too large. Try fewer cards per scan.");
  }
  if (status === 502 || status === 504) {
    return new Error("We couldn't read your card(s). Please try a clearer photo.");
  }
  return new Error("Card scanning is unavailable right now. Try again shortly.");
};

const toSaveError = (err: any): CardSaveError => {
  const reason = err?.response?.data?.error;
  const known: CardSaveError["reason"] =
    reason === "not_connected" || reason === "needs_reauth" || reason === "unavailable"
      ? reason
      : "unknown";
  return new CardSaveError(known, MESSAGES[known]);
};

/** POST /{customer_id}/receptionist/card-capture/extract */
export const extractCards = async (
  customerId: string,
  images: string[],
  idToken: string
): Promise<Contact[]> => {
  const id = requireCustomer(customerId);
  if (images.length > MAX_CARDS_PER_SCAN) {
    throw new Error(
      `At most ${MAX_CARDS_PER_SCAN} cards per scan — remove a few and try again.`
    );
  }
  let data: any;
  try {
    ({ data } = await axios.post(
      `${API_ROOT}/${id}/receptionist/card-capture/extract`,
      { images },
      authHeaders(idToken)
    ));
  } catch (err) {
    // Without this the panel renders axios's own "Request failed with status
    // code 403", which tells the user nothing they can act on.
    throw toExtractError(err);
  }
  return ((data?.contacts ?? []) as ExtractedContact[]).map((contact) => ({
    ...contact,
    id: newId(),
  }));
};

type CheckableContact = Omit<ExtractedContact, "issues">;

/** POST /{customer_id}/receptionist/card-capture/extract with contacts and no
 *  images (SGL-586): re-runs the server's field checks — the email domain's DNS
 *  records, the website on the card, a missing name — on contacts the owner has
 *  corrected. No model call, so it costs nothing. Returns one issue list per
 *  contact, in order. */
export const recheckCards = async (
  customerId: string,
  contacts: CheckableContact[],
  idToken: string
): Promise<CardIssue[][]> => {
  const id = requireCustomer(customerId);
  const { data } = await axios.post(
    `${API_ROOT}/${id}/receptionist/card-capture/extract`,
    {
      contacts: contacts.map(({ name, title, company, email, phone, mobile, website }) => ({
        name, title, company, email, phone, mobile, website,
      })),
    },
    authHeaders(idToken)
  );
  return ((data?.contacts ?? []) as { issues?: CardIssue[] }[]).map((c) =>
    Array.isArray(c?.issues) ? c.issues : []
  );
};

export interface EmailDraft {
  subject: string;
  body: string;
}

/** POST /{customer_id}/receptionist/card-capture/draft — compose only.
 *  Writes nothing and sends nothing, so the user can read and edit the intro
 *  before either happens. */
export const draftIntros = async (
  customerId: string,
  contacts: Contact[],
  captureContext: string,
  idToken: string
): Promise<{ drafts: (EmailDraft | null)[]; suggestedTag: string | null }> => {
  const id = requireCustomer(customerId);
  const { data } = await axios.post(
    `${API_ROOT}/${id}/receptionist/card-capture/draft`,
    { contacts: contacts.map(stripId), capture_context: captureContext },
    authHeaders(idToken)
  );
  const drafts = (data?.drafts ?? []) as (EmailDraft | null)[];
  return {
    drafts: contacts.map((_, i) => drafts[i] ?? null),
    suggestedTag: data?.suggested_tag ?? null,
  };
};

/** POST /{customer_id}/receptionist/card-capture/save — one card. */
export const saveCard = async (
  customerId: string,
  contact: Contact,
  captureContext: string,
  idToken: string,
  send: boolean = false,
  draft: EmailDraft | null = null,
  tags: string[] = []
): Promise<CardSaveOutcome> => {
  const id = requireCustomer(customerId);
  try {
    const { data } = await axios.post(
      `${API_ROOT}/${id}/receptionist/card-capture/save`,
      {
        contact: stripId(contact),
        capture_context: captureContext,
        tags,
        send,
        // The reviewed text, so what goes out is what the user approved.
        draft,
      },
      authHeaders(idToken)
    );
    return {
      ok: true,
      ghl: data?.ghl ?? null,
      draft: data?.draft ?? null,
      sent: data?.sent ?? null,
      sendError: data?.send_error ?? null,
      error: null,
    };
  } catch (err) {
    throw toSaveError(err);
  }
};

/** POST /{customer_id}/receptionist/card-capture/save-all — the whole batch.
 *  Per-contact failures come back inside `results`; only a connection-level
 *  failure throws. */
export const saveAllCards = async (
  customerId: string,
  contacts: Contact[],
  captureContext: string,
  idToken: string,
  send: boolean = false,
  drafts: (EmailDraft | null)[] = [],
  tags: string[] = []
): Promise<CardSaveOutcome[]> => {
  const id = requireCustomer(customerId);
  try {
    const { data } = await axios.post(
      `${API_ROOT}/${id}/receptionist/card-capture/save-all`,
      {
        contacts: contacts.map(stripId),
        capture_context: captureContext,
        tags,
        send,
        drafts,
      },
      authHeaders(idToken)
    );
    const byIndex = new Map<number, any>(
      (data?.results ?? []).map((r: any) => [r.index, r])
    );
    return contacts.map((_, index) => {
      const r = byIndex.get(index);
      return {
        ok: Boolean(r?.ok),
        ghl: r?.ghl ?? null,
        draft: r?.draft ?? null,
        sent: r?.sent ?? null,
        sendError: r?.send_error ?? null,
        error: r?.ok ? null : r?.error ?? "failed",
      };
    });
  } catch (err) {
    throw toSaveError(err);
  }
};

/** The `id` is a client-side React key; the backend's contact schema has no
 *  such field and ignores unknown keys, but sending it would be noise. */
const stripId = ({ id, ...rest }: Contact) => rest;
